import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@postdaily/ui/components/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@postdaily/ui/components/dropdown-menu";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { cn } from "@postdaily/ui/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, LayoutDashboard, LogOut, Settings } from "lucide-react";
import { toast } from "sonner";
import { authClient } from "@/lib/auth-client";
import { SIGN_IN_LINK, SIGN_UP_LINK } from "@/lib/auth-links";
import { resetAnalyticsIdentity } from "@/lib/posthog";

function useSignOut() {
  const queryClient = useQueryClient();

  return async () => {
    const toastId = toast.loading("Signing out...");

    try {
      await authClient.signOut();
      queryClient.clear();
      resetAnalyticsIdentity();
      toast.success("Signed out", { id: toastId });
      // Full page load — see the note in components/nav-user.tsx: the awaited
      // session refetch can resolve while the store still holds the old
      // session, so a client-side navigation would land on a page that still
      // believes the user is signed in.
      window.location.href = "/";
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : "Unable to sign out.",
        { id: toastId },
      );
    }
  };
}

function MarketingAuthSkeleton({ className }: { className?: string }) {
  return (
    <Skeleton className={cn("h-10 w-[148px] rounded-[10px]", className)} />
  );
}

function MarketingUserMenu({
  user,
}: {
  user: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
  };
}) {
  const handleSignOut = useSignOut();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<button type="button" aria-label="Account menu" />}
      >
        <Avatar size="lg">
          {user.image ? (
            <AvatarImage src={user.image} alt={user.name ?? ""} />
          ) : null}
          <AvatarFallback />
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        sideOffset={8}
        positionerClassName="z-[70]"
        className="pd-marketing-dropdown w-56 min-w-56 border-border shadow-lg"
      >
        <DropdownMenuGroup>
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar>
                {user.image ? (
                  <AvatarImage src={user.image} alt={user.name ?? ""} />
                ) : null}
                <AvatarFallback />
              </Avatar>
              <div className="grid min-w-0 flex-1 text-sm leading-tight">
                <span className="truncate font-medium text-foreground">
                  {user.name ?? "Account"}
                </span>
                {user.email ? (
                  <span className="truncate text-muted-foreground text-xs">
                    {user.email}
                  </span>
                ) : null}
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem render={<Link to="/dashboard" />}>
            <LayoutDashboard data-icon="inline-start" />
            Dashboard
          </DropdownMenuItem>
          <DropdownMenuItem render={<Link to="/settings" />}>
            <Settings data-icon="inline-start" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={handleSignOut}>
            <LogOut data-icon="inline-start" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const dashboardCtaClass =
  "pd-brand-grad inline-flex h-10 items-center gap-1.5 rounded-[10px] px-[17px] font-semibold text-[14.5px] text-white no-underline shadow-[0_6px_18px_-7px_oklch(0.55_0.16_268/0.6),inset_0_1px_0_oklch(1_0_0/0.2)] transition-transform hover:-translate-y-px";

function MarketingLoggedInActions({
  user,
  className,
}: {
  user: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
  };
  className?: string;
}) {
  return (
    <div className={cn("items-center gap-[9px]", className)}>
      <Link to="/dashboard" className={dashboardCtaClass}>
        Go to dashboard
        <ArrowRight className="size-[15px]" strokeWidth={2.2} />
      </Link>
      <MarketingUserMenu user={user} />
    </div>
  );
}

export function MarketingCompactAuthActions() {
  const { data: session, isPending } = authClient.useSession();

  if (isPending) {
    return (
      <Skeleton className="h-10 w-[148px] rounded-[10px] min-[900px]:hidden" />
    );
  }

  // Keep the bar to a single CTA next to the hamburger; account actions live in
  // the mobile drawer (`MarketingMobileAuthActions`).
  if (session) {
    return (
      <Link
        to="/dashboard"
        className={cn(dashboardCtaClass, "min-[900px]:hidden")}
      >
        Go to dashboard
      </Link>
    );
  }

  return (
    <Link
      {...SIGN_UP_LINK}
      className={cn(dashboardCtaClass, "min-[900px]:hidden")}
    >
      Start for free
    </Link>
  );
}

export function MarketingDesktopAuthActions() {
  const { data: session, isPending } = authClient.useSession();

  if (isPending) {
    return (
      <MarketingAuthSkeleton className="hidden h-10 w-[220px] min-[900px]:block" />
    );
  }

  if (session) {
    return (
      <MarketingLoggedInActions
        user={session.user}
        className="hidden min-[900px]:flex"
      />
    );
  }

  return (
    <div className="hidden items-center gap-[9px] min-[900px]:flex">
      <Link
        {...SIGN_IN_LINK}
        className="inline-flex h-10 items-center rounded-[10px] px-3.5 font-semibold text-[14.5px] text-[oklch(0.3_0.02_264)] no-underline transition-colors hover:bg-[oklch(0.95_0.014_264)]"
      >
        Sign in
      </Link>
      <Link {...SIGN_UP_LINK} className={dashboardCtaClass}>
        Start for free
        <ArrowRight className="size-[15px]" strokeWidth={2.2} />
      </Link>
    </div>
  );
}

export function MarketingMobileAuthActions({
  onNavigate,
}: {
  onNavigate?: () => void;
}) {
  const { data: session, isPending } = authClient.useSession();
  const handleSignOut = useSignOut();

  if (isPending) {
    return (
      <div className="mt-3.5">
        <MarketingAuthSkeleton className="h-[46px] w-full rounded-[11px]" />
      </div>
    );
  }

  if (session) {
    return (
      <div className="mt-3.5 flex flex-col gap-2.5">
        <Link
          to="/dashboard"
          onClick={onNavigate}
          className="pd-brand-grad inline-flex h-[46px] w-full items-center justify-center rounded-[11px] font-semibold text-[15px] text-white no-underline"
        >
          Go to dashboard
        </Link>
        <button
          type="button"
          onClick={() => {
            onNavigate?.();
            void handleSignOut();
          }}
          className="inline-flex h-[46px] w-full items-center justify-center rounded-[11px] border border-border font-semibold text-[15px] text-foreground"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3.5 flex gap-2.5">
      <Link
        {...SIGN_IN_LINK}
        onClick={onNavigate}
        className="inline-flex h-[46px] flex-1 items-center justify-center rounded-[11px] border border-border font-semibold text-[15px] text-foreground no-underline"
      >
        Sign in
      </Link>
      <Link
        {...SIGN_UP_LINK}
        onClick={onNavigate}
        className="pd-brand-grad inline-flex h-[46px] flex-1 items-center justify-center rounded-[11px] font-semibold text-[15px] text-white no-underline"
      >
        Start for free
      </Link>
    </div>
  );
}
